import { Icon } from "@cadmakers-ui/icons";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Table } from "reactstrap";
import { DataGrid } from "@cadmakers/data-grid-v2";
import iconStyles from "../../Common/Styles/Icons.module.scss";
import { endpoints } from "../Constants/endpoints";
import ResponseHandler from "../../App/ResponseHandler";
import {
  ApiRequest,
  ErrorDisplay,
  IAppState,
  LoadingDisplay,
  LoadingState,
  ResponseError,
  useShowSnack
} from "../../Common";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import { IconButton, ModalHeader, Modal, ModalBody, ModalFooter } from "../../Layout";
import { Topic } from "../Models/Topic";
import { UserTopicAssignment } from "../Models/UserTopicAssignment";
import { TopicUsersAutocomplete } from "./TopicUsersAutocomplete";
import { TopicUsersAutocompleteType } from "../Enums/TopicUsersAutocompleteType";
import { TopicUserAutoCompleteItem } from "../Models/TopicUserAutoCompleteItem";
import useTopicUsers from "../Hooks/useTopicUsers";

type Props = {
  topic: Topic;
  toggle: boolean;
  onToggle: () => void;
};

const AssignTopicUsersModal: React.FC<Props> = props => {
  const [filter, setFilter] = useState("");
  const [lock, setLock] = useState(false);

  const showSnack = useShowSnack();

  const {
    userLoadState,
    userLoadError,
    users,
    setLoadingTopicUsers,
    getTopicUsers,
    setTopicUsers
  } = useTopicUsers(props.topic.code);

  const ensureUsersLoaded = () => {
    if (
      !userLoadState ||
      (!userLoadState.isLoading && !userLoadState.isLoaded && !userLoadState.errored)
    ) {
      setLoadingTopicUsers();
      getTopicUsers();
    }
  };

  // useEffect which makes sure users are loaded when toggled
  useEffect(() => {
    ensureUsersLoaded();
  }, [props.toggle]);

  const claimLock = () => {
    setLock(true);
  };

  const releaseLock = () => {
    setLock(false);
  };

  const onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.currentTarget.value);
  };

  const showErrorSnackbar = (reason: string, errorDescription: string, errorCode?: number) => {
    const label = `${reason}: ${errorDescription} ${errorCode ? `(Error Code ${errorCode})` : ""}`;

    showSnack("ASSIGN_TOPIC_USRS_MODAL", {
      label,
      timeout: 5000
    });
  };

  const setError = (
    result: string,
    description: string,
    code?: number,
    shouldShowSnack: boolean = false
  ) => {
    if (shouldShowSnack) {
      showErrorSnackbar(result, description, code);
    }
  };

  const onSelectUser = (user: TopicUserAutoCompleteItem) => {
    if (!lock) {
      claimLock();

      ApiRequest.fromPath(endpoints.topics.assignUser(props.topic.code))
        .withAuthHeader()
        .withJson({ username: user.username })
        .put()
        .then(response => {
          ResponseHandler.handle(
            response,
            () => {
              if (!!users && users.find(u => u.user.username === user.username)) {
                return;
              }

              setTopicUsers([
                ...(users || []),
                {
                  user: {
                    id: user.userId,
                    name: user.name,
                    email: user.email,
                    username: user.username
                  },
                  allowStateChange: false
                } as UserTopicAssignment
              ]);
            },
            result => {
              setError("Failed to assign user", result.error.message, response.status, true);
            },
            () => {
              setError("Failed to assign user", "An unknown error has occured", undefined, true);
            }
          );
        })
        .catch(() => {
          setError("Failed to assign user", "Failed to contact server", undefined, true);
        })
        .finally(() => releaseLock());
    }
  };

  const unassignUser = (username: string) => {
    ApiRequest.fromPath(endpoints.topics.unassignUser(props.topic.code))
      .withAuthHeader()
      .withJson({ username })
      .delete()
      .then(response => {
        ResponseHandler.handle(
          response,
          () => {
            setTopicUsers(users ? users.filter(a => a.user.username !== username) : []);
          },
          result => {
            setError("Failed to unassign user", result.error.message, response.status, true);
          },
          () => {
            setError("Failed to unassign user", "An unknown error has occured", undefined, true);
          }
        );
      })
      .catch(() => {
        setError("Failed to unassign user", "Failed to contact server", undefined, true);
      });
  };

  const dataForTable = useMemo(
    () =>
      (users || []).map((u, i) => ({
        index: i,
        name: u.user.name,
        email: u.user.email,
        username: u.user.username,
        actions: { username: u.user.username }
      })),
    [users]
  );

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessor: "name",
        type: "text",
        searchable: true,
        sortable: true,
        filterable: true
      },
      {
        header: "Email",
        accessor: "email",
        type: "text",
        searchable: true,
        sortable: true,
        filterable: true
      },
      {
        header: "",
        accessor: "actions",
        sortable: false,
        searchable: false,
        render: ({ value }: any) => {
          return (
            <IconButton
              icon={
                <Icon
                  appName="GeneralLayout"
                  assetName="GENERAL_L_DeleteTrash"
                  onClick={() => unassignUser(value.username)}
                  className={iconStyles.smallIcon}
                />
              }
            />
          );
        },
        visibleInExport: false
      }
    ],
    []
  );

  const renderModalBody = () => {
    if (!userLoadState || userLoadState.isLoading) {
      return <LoadingDisplay />;
    }

    if (userLoadState.errored) {
      if (!userLoadError) {
        return (
          <ErrorDisplay
            errorDescription="Error message not specified"
            errorResult="Could not load users"
          />
        );
      }

      return (
        <ErrorDisplay
          errorCode={userLoadError.errorCode}
          errorDescription={userLoadError.errorDescription}
          errorResult={userLoadError.errorResult}
        />
      );
    }

    return (
      <>
        <TopicUsersAutocomplete
          topic={props.topic}
          placeHolder="Search users"
          onItemSelect={onSelectUser}
          ignoreExistingUsers
          autocompleteFor={TopicUsersAutocompleteType.User}
        />
        <br />
        <Input
          type="text"
          placeholder="Filter assigned users"
          value={filter}
          onChange={onFilterChange}
        />
        <br />

        <DataGrid data={dataForTable} columns={columns as any[]} exportCsv disableVirtualization />
      </>
    );
  };

  return (
    <Modal isOpen={props.toggle} onToggle={props.onToggle}>
      <ModalHeader toggle={props.onToggle}>Assign Users To Topic</ModalHeader>
      <ModalBody>{renderModalBody()}</ModalBody>
      <ModalFooter>
        <Button onClick={props.onToggle}>Done</Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignTopicUsersModal;
